import { FormControl, FormGroup } from '@angular/forms';
import { FormGroupRawValue } from '../../helpers/types.utils';
import {
  createMessageTypeField,
  createSendToUsersField,
  createStateCodesField,
  createSendToRolesField,
  createMessageContentField,
  createMessageSubjectField,
  createSaveAsDraftField,
  createScheduleToField
} from '../fields/broadcast-message-fields';
import { MessageTypeEnum } from '../../enums';

/**
 * Interface for Broadcast Message creation DTO
 * Matches the API CreateBroadcastMessageDto structure
 */
export interface CreateBroadcastMessageDtoForm {
  type: MessageTypeEnum;
  sendToUsers: string[];
  sendToStates?: string[];
  sendToRoles?: string[];
  subject: string;
  message: string;
  scheduleTo?: string | null;
  saveAsDraft?: boolean;
}

/**
 * Interface for Broadcast Message creation form value
 * Corresponds to CreateBroadcastMessageDto in backend
 * Used for: New Communication dialog
 */
export type CreateBroadcastMessageFormValue =
  FormGroupRawValue<CreateBroadcastMessageFormGroup>;

/**
 * Type definition for the CreateBroadcastMessage FormGroup structure
 */
export type CreateBroadcastMessageFormGroup = FormGroup<{
  type: FormControl<MessageTypeEnum>;
  sendToUsers: ReturnType<typeof createSendToUsersField>;
  sendToStates: ReturnType<typeof createStateCodesField>;
  sendToRoles: ReturnType<typeof createSendToRolesField>;
  subject: ReturnType<typeof createMessageSubjectField>;
  message: ReturnType<typeof createMessageContentField>;
  scheduleTo: ReturnType<typeof createScheduleToField>;
  saveAsDraft: ReturnType<typeof createSaveAsDraftField>;
}>;

/**
 * Creates a complete FormGroup for Broadcast Message creation
 * Groups all information required for POST /api/messages
 *
 * @param initial - Optional initial values for form fields
 * @returns FormGroup with all broadcast message fields organized into logical groups
 *
 * @example
 * ```typescript
 * const form = createBroadcastMessageForm({
 *   targeting: { type: 'Email', sendToUsers: ['all'] },
 *   content: { subject: 'Hello', message: 'World' },
 *   schedule: { scheduleTo: null, saveAsDraft: false }
 * });
 * ```
 */
export function createBroadcastMessageForm(
  initial?: Partial<CreateBroadcastMessageFormValue>
): CreateBroadcastMessageFormGroup {
  return new FormGroup({
    type: createMessageTypeField({ required: true }, initial?.type),
    sendToUsers: createSendToUsersField(
      { required: true },
      initial?.sendToUsers
    ),
    sendToStates: createStateCodesField(initial?.sendToStates),
    sendToRoles: createSendToRolesField(initial?.sendToRoles),
    subject: createMessageSubjectField({ required: true }, initial?.subject),
    message: createMessageContentField({ required: true }, initial?.message),
    scheduleTo: createScheduleToField(
      initial?.scheduleTo as string | null | undefined
    ),
    saveAsDraft: createSaveAsDraftField(
      initial?.saveAsDraft as boolean | undefined
    )
  });
}

/**
 * Converts form value to CreateBroadcastMessageDtoForm
 * Maps the grouped form structure to the flat API DTO structure
 */
export function formValueToCreateBroadcastMessageDto(
  formValue: CreateBroadcastMessageFormValue
): CreateBroadcastMessageDtoForm {
  return {
    type: formValue.type ?? MessageTypeEnum.Email,
    sendToUsers: formValue.sendToUsers ?? ['all'],
    sendToStates:
      formValue.sendToStates && formValue.sendToStates.length > 0
        ? formValue.sendToStates
        : undefined,
    sendToRoles:
      formValue.sendToRoles && formValue.sendToRoles.length > 0
        ? formValue.sendToRoles
        : undefined,
    subject: formValue.subject ?? '',
    message: formValue.message ?? '',
    scheduleTo: (formValue.scheduleTo as string | null) ?? null,
    saveAsDraft: (formValue.saveAsDraft as boolean) ?? false
  };
}

/**
 * Creates initial form value from DTO
 * Maps the flat API DTO structure to the grouped form structure
 */
export function dtoToFormValue(
  dto: Partial<CreateBroadcastMessageDtoForm>
): Partial<CreateBroadcastMessageFormValue> {
  return {
    type: dto.type,
    sendToUsers: dto.sendToUsers,
    sendToStates: dto.sendToStates,
    sendToRoles: dto.sendToRoles,
    subject: dto.subject,
    message: dto.message,
    scheduleTo: dto.scheduleTo,
    saveAsDraft: dto.saveAsDraft
  };
}
